<script setup lang="ts">
/* ------------------------------------------------------------------------------------------------
 * Onboarding coach-mark overlay.
 *
 * Dims the page, cuts a spotlight around the current step's target element, and floats a
 * Luma-branded tooltip beside it (positioned with floating-ui). Driven by use-onboarding.
 * Mounted once, globally, in App.vue.
 * ---------------------------------------------------------------------------------------------- */

import type { Placement } from '@floating-ui/vue';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import { Icon } from '@iconify/vue';
import { onKeyStroke, useRafFn } from '@vueuse/core';
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import seahorse from '@/assets/images/logo/logo-luma-seahorse.png';
import { useOnboarding } from '@/composables/use-onboarding.ts';

const {
   isActive,
   currentStep,
   currentIndex,
   stepCount,
   isFirst,
   isLast,
   steps,
   next,
   prev,
   skip,
   finish,
   goTo,
   maybeAutoStart,
} = useOnboarding();

const reference = ref<HTMLElement | null>(null);
const floatingEl = ref<HTMLElement | null>(null);
const placement = ref<Placement>('bottom');
const spotlightRect = ref<{ top: number; left: number; width: number; height: number } | null>(null);
// The element focused before the tour opened, so focus can be restored on close.
const previouslyFocused = ref<HTMLElement | null>(null);

const PADDING = 8;

const { floatingStyles } = useFloating(reference, floatingEl, {
   placement,
   middleware: [offset(14), flip({ padding: 12 }), shift({ padding: 12 })],
   whileElementsMounted: autoUpdate,
});

/* ---------------------------------------------------------------------------------------------- */

function updateRect() {
   const el = reference.value;
   if (!el) {
      spotlightRect.value = null;
      return;
   }
   const r = el.getBoundingClientRect();
   spotlightRect.value = { top: r.top, left: r.left, width: r.width, height: r.height };
}

// Keep the spotlight glued to the target while the tour is open (covers scroll,
// layout shifts, and the smooth scroll-into-view animation).
const { pause, resume } = useRafFn(updateRect, { immediate: false });

/**
 * Point the overlay at the current step's target. Steps whose target isn't on the
 * page (e.g. replaying the tour from a different route) are skipped gracefully.
 */
async function syncToStep(): Promise<void> {
   if (!isActive.value)
      return;

   await nextTick();
   const step = currentStep.value;
   if (!step) {
      finish();
      return;
   }

   const el = document.querySelector<HTMLElement>(step.target);
   if (!el) {
      if (isLast.value)
         finish();
      else
         goTo(currentIndex.value + 1);
      return;
   }

   el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
   await nextTick();

   reference.value = el;
   placement.value = (step.placement as Placement) ?? 'bottom';
   updateRect();

   // Move focus into the dialog so keyboard users land on its controls.
   await nextTick();
   floatingEl.value?.focus();
}

watch([isActive, currentIndex], (_new, [prevActive]) => {
   if (isActive.value) {
      if (!prevActive)
         previouslyFocused.value = document.activeElement as HTMLElement | null;
      resume();
      syncToStep();
   }
   else {
      pause();
      reference.value = null;
      spotlightRect.value = null;
      // Restore focus to whatever opened the tour.
      previouslyFocused.value?.focus?.();
      previouslyFocused.value = null;
   }
});

onKeyStroke('Escape', () => {
   if (isActive.value)
      skip();
});

// Auto-run once, on desktop only (the tour points at the left sidebar, which is
// hidden on small screens).
let autoStartTimer: number | undefined;

onMounted(() => {
   autoStartTimer = window.setTimeout(() => {
      if (!window.matchMedia('(min-width: 1024px)').matches)
         return;
      // Only auto-start when the first step's target is actually on the page.
      // Otherwise syncToStep would immediately finish the tour and burn the
      // one-time "seen" flag without the user ever seeing anything.
      const firstTarget = steps.value[0]?.target;
      if (firstTarget && document.querySelector(firstTarget))
         maybeAutoStart();
   }, 900);
});

onUnmounted(() => {
   if (autoStartTimer !== undefined)
      window.clearTimeout(autoStartTimer);
});
</script>

<template>
   <Teleport to="body">
      <div v-if="isActive" class="fixed inset-0 z-[9000]">
         <!-- Spotlight: transparent hole with a huge box-shadow dimming everything else -->
         <div
            v-if="spotlightRect"
            class="onboarding-spotlight"
            :style="{
               top: `${spotlightRect.top - PADDING}px`,
               left: `${spotlightRect.left - PADDING}px`,
               width: `${spotlightRect.width + PADDING * 2}px`,
               height: `${spotlightRect.height + PADDING * 2}px`,
            }"
         />

         <!-- Tooltip -->
         <div
            v-if="currentStep"
            ref="floatingEl"
            :style="floatingStyles"
            class="onboarding-tooltip w-[320px] max-w-[calc(100vw-2rem)] rounded-2xl bg-white p-4
            shadow-[0_24px_60px_rgba(2,77,80,0.28)] border border-black/5"
            role="dialog"
            aria-modal="true"
            tabindex="-1"
            :aria-label="currentStep.title"
         >
            <div class="flex items-center gap-2.5 mb-2">
               <div class="grid place-items-center size-8 rounded-full bg-[#EAF4F3] shrink-0">
                  <img :src="seahorse" alt="" class="h-5 w-auto object-contain" />
               </div>
               <span class="text-xs font-medium text-black/45">
                  {{ currentIndex + 1 }} of {{ stepCount }}
               </span>
               <button
                  type="button"
                  class="ml-auto grid place-items-center size-7 rounded-full text-black/40 hover:bg-black/5
                  hover:text-black transition-colors"
                  aria-label="Skip tour"
                  @click="skip"
               >
                  <Icon icon="ph:x" class="size-4" />
               </button>
            </div>

            <h3 class="text-base font-semibold text-[#0B3436]">
               {{ currentStep.title }}
            </h3>
            <p class="mt-1 text-sm text-black/60 leading-relaxed">
               {{ currentStep.body }}
            </p>

            <div class="flex items-center justify-between mt-4">
               <!-- Progress dots -->
               <div class="flex items-center gap-1.5">
                  <span
                     v-for="(s, i) in steps"
                     :key="s.id"
                     class="size-1.5 rounded-full transition-colors"
                     :class="i === currentIndex ? 'bg-primary' : 'bg-black/15'"
                  />
               </div>

               <div class="flex items-center gap-2">
                  <button
                     v-if="!isFirst"
                     type="button"
                     class="px-3 py-1.5 rounded-lg text-sm font-medium text-black/60 hover:bg-black/5 transition-colors"
                     @click="prev"
                  >
                     Back
                  </button>
                  <button
                     type="button"
                     class="px-4 py-1.5 rounded-lg text-sm font-semibold text-white bg-primary
                     hover:bg-primary-light transition-colors"
                     @click="next"
                  >
                     {{ isLast ? 'Done' : 'Next' }}
                  </button>
               </div>
            </div>
         </div>
      </div>
   </Teleport>
</template>

<style scoped lang="postcss">
.onboarding-spotlight {
   position: fixed;
   border-radius: 14px;
   pointer-events: none;
   box-shadow:
      0 0 0 3px rgba(45, 182, 154, 0.6),
      0 0 0 9999px rgba(6, 34, 35, 0.55);
   transition: top 150ms ease, left 150ms ease, width 150ms ease, height 150ms ease;
}
</style>
